import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import Iconify from 'src/components/Iconify';
import { piiProps } from 'src/components/DemoBlur';
import { LEAD_PIN_REASON_MAX } from 'src/redux/slices/leadPins';

// -----------------------------------------------
// Pin a lead (or edit an existing pin's note). The note is optional and only
// ever shown on the pin card / chip tooltip.

// "popup › step · 14:32" — step and time are optional.
const describeFailureContext = (context = {}) => {
    const where = [context.popup, context.step].filter(Boolean).join(' › ');
    const when = context.at && !Number.isNaN(new Date(context.at).getTime())
        ? new Date(context.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : '';
    return [where, when].filter(Boolean).join(' · ');
};

export default function PinLeadDialog({ open, mode, initialReason, onClose, onSubmit, loading, attachedError }) {
    const [reason, setReason] = useState(initialReason || '');

    // Re-seed each time the dialog opens so a cancelled edit doesn't linger.
    useEffect(() => {
        if (open) setReason(initialReason || '');
    }, [open, initialReason]);

    const isEdit = mode === 'edit';
    const tooLong = reason.length > LEAD_PIN_REASON_MAX;

    const handleSubmit = () => {
        if (tooLong || loading) return;
        onSubmit(reason.trim());
    };

    return (
        <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Iconify icon={isEdit ? 'mdi:pencil' : 'mdi:pin'} width={22} sx={{ color: 'primary.main' }} />
                {isEdit ? 'Edit pin note' : 'Pin lead'}
            </DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={8}
                    size="small"
                    label="Why are you pinning this lead? (optional)"
                    value={reason}
                    onChange={(e) => setReason(e.target.value.slice(0, LEAD_PIN_REASON_MAX))}
                    inputProps={{ maxLength: LEAD_PIN_REASON_MAX, 'data-testid': 'pin-lead-reason' }}
                    helperText={`${reason.length}/${LEAD_PIN_REASON_MAX}`}
                    FormHelperTextProps={{ sx: { textAlign: 'right', mr: 0 } }}
                    error={tooLong}
                    sx={{ mt: 1 }}
                />
                {attachedError && (
                    <Box data-testid="pin-attached-error" sx={{ mt: 2, p: 1.5, borderRadius: '8px', bgcolor: 'grey.100' }}>
                        <Typography variant="subtitle2">Error attached automatically</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, wordBreak: 'break-word' }} {...piiProps}>{attachedError.message}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            {describeFailureContext(attachedError.context)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                            Shared with the DentalSEM support team (super admins) so they can fix it.
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
                <Stack direction="row" spacing={1} sx={{ width: '100%' }} justifyContent="flex-end">
                    <Button variant="outlined" color="inherit" onClick={onClose} disabled={loading} sx={{ textTransform: 'none', borderRadius: '8px' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={loading || tooLong}
                        startIcon={<Iconify icon={isEdit ? 'eva:checkmark-fill' : 'mdi:pin'} width={16} />}
                        sx={{ textTransform: 'none', borderRadius: '8px' }}
                    >
                        {isEdit ? 'Save' : 'Pin'}
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
}

PinLeadDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    mode: PropTypes.oneOf(['create', 'edit']),
    initialReason: PropTypes.string,
    loading: PropTypes.bool,
    attachedError: PropTypes.shape({
        message: PropTypes.string,
        context: PropTypes.shape({ popup: PropTypes.string, step: PropTypes.string, at: PropTypes.string }),
    }),
};

PinLeadDialog.defaultProps = {
    mode: 'create',
    initialReason: '',
    loading: false,
    attachedError: undefined,
};
